"use strict";
//
// Welcome to the annotated FaceTec Device SDK core code for performing secure Authentication against a previously enrolled user.
//
Object.defineProperty(exports, "__esModule", { value: true });
exports.AuthenticateProcessor = void 0;
const Config_1 = require("../../../app/Config");
const FaceTecSDK_1 = require("./../../../core-sdk/FaceTecSDK.js/FaceTecSDK");
const DeveloperStatusMessages_1 = require("../utilities/DeveloperStatusMessages");
const auth_1 = require("../../../app/utils/auth");
//
// This is an example self-contained class to perform Authentication with the FaceTec SDK.
// You may choose to further componentize parts of this in your own Apps based on your specific requirements.
//
class AuthenticateProcessor {
    constructor(sessionToken, sampleAppControllerReference) {
        this.latestNetworkRequest = new XMLHttpRequest();
        //
        // Part 10:  This function gets called after the FaceTec SDK is completely done.  There are no parameters because you have already been passed all data in the processSessionWhileFaceTecSDKWaits function and have already handled all of your own results.
        //
        this.onFaceTecSDKCompletelyDone = () => {
            //
            // DEVELOPER NOTE:  onFaceTecSDKCompletelyDone() is called after you signal the FaceTec SDK with success() or cancel().
            // Calling a custom function on the Sample App Controller is done for demonstration purposes to show you that here is where you get control back from the FaceTec SDK.
            //
            this.success = this.latestSessionResult.isCompletelyDone;
            // Log success message and dispatch Redux action
            if (this.success) {
                DeveloperStatusMessages_1.DeveloperStatusMessages.logMessage("Authenticated");
            }
            else {
                // Dispatch failure action to Redux with error context
                console.log("FaceTec authentication failed, resetting progress state");
                const errorMsg = this.cancelledDueToNetworkError
                    ? "Authentication failed due to a network error. Please check your connection and try again."
                    : "Face authentication could not be completed. Please try again or contact support.";
                (0, auth_1.failedAuthenticateUser)(errorMsg);
            }
            this.sampleAppControllerReference.onComplete(this.latestSessionResult, null, this.latestNetworkRequest.status);
        };
        // Helper function to ensure the session is only cancelled once
        this.cancelDueToNetworkError = (networkErrorMessage, faceScanResultCallback) => {
            if (this.cancelledDueToNetworkError === false) {
                console.log(networkErrorMessage);
                this.cancelledDueToNetworkError = true;
                faceScanResultCallback.cancel();
            }
        };
        //
        // DEVELOPER NOTE:  This public convenience method is for demonstration purposes only so the Sample App can get information about what is happening in the processor.
        // In your code, you may not even want or need to do this.
        //
        this.isSuccess = () => {
            return this.success;
        };
        //
        // DEVELOPER NOTE:  These properties are for demonstration purposes only so the Sample App can get information about what is happening in the processor.
        // In the code in your own App, you can pass around signals, flags, intermediates, and results however you would like.
        //
        this.success = false;
        this.sampleAppControllerReference = sampleAppControllerReference;
        this.latestSessionResult = null;
        this.cancelledDueToNetworkError = false;
        //
        // Part 1:  Starting the FaceTec Session
        //
        // Required parameters:
        // - FaceTecFaceScanProcessor:  A class that implements FaceTecFaceScanProcessor, which handles the FaceScan when the User completes a Session.  In this example, "this" implements the class.
        // - sessionToken:  A valid Session Token you just created by calling your API to get a Session Token from the Server SDK.
        //
        new FaceTecSDK_1.FaceTecSDK.FaceTecSession(this, sessionToken);
    }
    //
    // Part 2:  Handling the Result of a FaceScan
    //
    processSessionResultWhileFaceTecSDKWaits(sessionResult, faceScanResultCallback) {
        this.latestSessionResult = sessionResult;
        //
        // Part 3:  Handles early exit scenarios where there is no FaceScan to handle -- i.e. User Cancellation, Timeouts, etc.
        //
        if (sessionResult.status !== FaceTecSDK_1.FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully) {
            const statusName = FaceTecSDK_1.FaceTecSDK.FaceTecSessionStatus[sessionResult.status];
            console.log("Session was not completed successfully, cancelling.  Session Status: " + statusName);
            this.latestNetworkRequest.abort();
            faceScanResultCallback.cancel();
            return;
        }
        // IMPORTANT:  FaceTecSDK.FaceTecSessionStatus.SessionCompletedSuccessfully DOES NOT mean the Authenticate was Successful.
        // It simply means the User completed the Session and a 3D FaceScan was created.  You still need to perform the Authentication on your Servers.
        //
        // Part 4:  Get essential data off the FaceTecSessionResult
        //
        //Get
        var parameters = {
            faceScan: sessionResult.faceScan,
            auditTrailImage: sessionResult.auditTrail[0],
            lowQualityAuditTrailImage: sessionResult.lowQualityAuditTrail[0],
            // sessionId: sessionResult.sessionId,
            externalDatabaseRefID: this.sampleAppControllerReference.getLatestEnrollmentIdentifier(),
        };
        //
        // Part 5:  Make the Networking Call to Your Servers.  Below is just example code, you are free to customize based on how your own API works.
        //
        //TODO - Add .content
        this.latestNetworkRequest = new XMLHttpRequest();
        this.latestNetworkRequest.open("POST", Config_1.Config.BaseURL + "/match-3d-3d");
        this.latestNetworkRequest.setRequestHeader("Content-Type", "application/json");
        this.latestNetworkRequest.setRequestHeader("X-Device-Key", Config_1.Config.DeviceKeyIdentifier);
        this.latestNetworkRequest.setRequestHeader("X-User-Agent", FaceTecSDK_1.FaceTecSDK.createFaceTecAPIUserAgentString(sessionResult.sessionId));
        this.latestNetworkRequest.onreadystatechange = () => {
            //
            // Part 6:  In our Sample, we evaluate a boolean response and treat true as was successfully processed and should proceed to next step,
            // and handle all other responses by cancelling out.
            // You may have different paradigms in your own API and are free to customize based on these.
            //
            var _a, _b, _c;
            if (this.latestNetworkRequest.readyState === XMLHttpRequest.DONE) {
                try {
                    const fullResponse = JSON.parse((_a = this.latestNetworkRequest) === null || _a === void 0 ? void 0 : _a.responseText);
                    const responseJSON = fullResponse === null || fullResponse === void 0 ? void 0 : fullResponse.content;
                    const scanResultBlob = responseJSON === null || responseJSON === void 0 ? void 0 : responseJSON.scanResultBlob;
                    console.log("FaceTec API response:", {
                        wasProcessed: responseJSON === null || responseJSON === void 0 ? void 0 : responseJSON.wasProcessed,
                        success: responseJSON === null || responseJSON === void 0 ? void 0 : responseJSON.success,
                        hasScanResultBlob: !!scanResultBlob,
                    });
                    // In v9.2.0+, we key off a new property called wasProcessed to determine if we successfully processed the Session result on the Server.
                    // Device SDK UI flow is now driven by the proceedToNextStep function, which should receive the scanResultBlob from the Server SDK response.
                    if (responseJSON === null || responseJSON === void 0 ? void 0 : responseJSON.wasProcessed) {
                        // Dispatch to Redux via helper function (matches CRM pattern)
                        if (responseJSON.success) {
                            (0, auth_1.successfullyAuthenticateUser)();
                        }
                        // Demonstrates dynamically setting the Success Screen Message.
                        FaceTecSDK_1.FaceTecSDK.FaceTecCustomization.setOverrideResultScreenSuccessMessage("Authenticated");
                        // In v9.2.0+, simply pass in scanResultBlob to the proceedToNextStep function to advance the User flow.
                        // scanResultBlob is a proprietary, encrypted blob that controls the logic for what happens next for the User.
                        faceScanResultCallback.proceedToNextStep(scanResultBlob);
                    }
                    else {
                        // Check for specific enrollment not found error
                        if ((_b = responseJSON === null || responseJSON === void 0 ? void 0 : responseJSON.errorMessage) === null || _b === void 0 ? void 0 : _b.includes("enrollment does not exist")) {
                            console.error("User not enrolled in FaceTec, cancelling authentication");
                            // Pass specific error to Redux so FaceTecGate can show enrolment prompt
                            (0, auth_1.failedAuthenticateUser)("Your Facial Recognition data was not found. You may need to re-enrol. Please try again or contact support.");
                            this.cancelDueToNetworkError("You are not enrolled in Facial Recognition. Please enrol first.", faceScanResultCallback);
                            return;
                        }
                        // CASE:  UNEXPECTED response from API.  Our Sample Code keys off a wasProcessed boolean on the root of the JSON object --> You define your own API contracts with yourself and may choose to do something different here based on the error.
                        console.error("Unexpected API response:", responseJSON);
                        this.cancelDueToNetworkError("Unexpected API response, cancelling out.", faceScanResultCallback);
                    }
                }
                catch (error) {
                    console.error("Exception parsing API response:", error);
                    console.error("Raw response:", (_c = this.latestNetworkRequest) === null || _c === void 0 ? void 0 : _c.responseText);
                    // CASE:  Parsing the response into JSON failed --> You define your own API contracts with yourself and may choose to do something different here based on the error.  Solid server-side code should ensure you don't get to this case.
                    this.cancelDueToNetworkError("Exception while handling API response, cancelling out.", faceScanResultCallback);
                }
            }
        };
        this.latestNetworkRequest.onerror = () => {
            // CASE:  Network Request itself is erroring --> You define your own API contracts with yourself and may choose to do something different here based on the error.
            this.cancelDueToNetworkError("XHR error, cancelling.", faceScanResultCallback);
        };
        //
        // Part 7:  Demonstrates updating the Progress Bar based on the progress event.
        //
        this.latestNetworkRequest.upload.onprogress = (event) => {
            var progress = event.loaded / event.total;
            faceScanResultCallback.uploadProgress(progress);
        };
        //
        // Part 8:  Actually send the request.
        //
        var jsonStringToUpload = JSON.stringify(parameters);
        this.latestNetworkRequest.send(jsonStringToUpload);
        //
        // Part 9:  For better UX, update the User if the upload is taking a while.  You are free to customize and enhance this behavior to your liking.
        //
        window.setTimeout(() => {
            if (this.latestNetworkRequest.readyState === XMLHttpRequest.DONE) {
                return;
            }
            faceScanResultCallback.uploadMessageOverride("Still Uploading...");
        }, 6000);
    }
}
exports.AuthenticateProcessor = AuthenticateProcessor;
